import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@comandero/shared";

/**
 * Ilustraciones SVG inline para empty states, con identidad gastronómica.
 *
 * Todas son theme-aware: usan `currentColor` (tono del contenedor) y `fill`
 * con baja opacidad para el relleno, de modo que heredan el color del tema
 * sin tonos hardcodeados. Decorativas → `aria-hidden`.
 */
type Illustration = "tables" | "menu" | "orders" | "plate" | "reservation";

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "1.5",
  "stroke-linecap": "round" as const,
  "stroke-linejoin": "round" as const,
};

/**
 * Mapa de ilustraciones → grupo de paths SVG. Data-driven (sin early returns)
 * para mantener la reactividad de Solid: el `<svg>` se renderiza una sola vez y
 * el contenido se elige reactivamente por `name`.
 */
const SHAPES: Record<Illustration, () => JSX.Element> = {
  tables: () => (
    <>
      <circle cx="48" cy="40" r="20" fill="currentColor" opacity="0.08" />
      <circle cx="48" cy="40" r="20" {...strokeProps} />
      <path d="M48 60v16" {...strokeProps} />
      <path d="M36 84h24" {...strokeProps} />
      <circle cx="24" cy="34" r="5" {...strokeProps} />
      <circle cx="72" cy="34" r="5" {...strokeProps} />
    </>
  ),
  menu: () => (
    <>
      <rect x="26" y="18" width="44" height="60" rx="5" fill="currentColor" opacity="0.08" />
      <rect x="26" y="18" width="44" height="60" rx="5" {...strokeProps} />
      <path d="M36 34h24M36 44h24M36 54h16" {...strokeProps} />
    </>
  ),
  orders: () => (
    <>
      <path
        d="M30 22h36l-3 50a4 4 0 0 1-4 4H37a4 4 0 0 1-4-4z"
        fill="currentColor"
        opacity="0.08"
      />
      <path d="M30 22h36l-3 50a4 4 0 0 1-4 4H37a4 4 0 0 1-4-4z" {...strokeProps} />
      <path d="M24 22h48" {...strokeProps} />
      <path d="M40 16h16v6H40z" {...strokeProps} />
      <path d="M42 40v22M54 40v22" {...strokeProps} />
    </>
  ),
  reservation: () => (
    <>
      <rect x="22" y="26" width="52" height="48" rx="6" fill="currentColor" opacity="0.08" />
      <rect x="22" y="26" width="52" height="48" rx="6" {...strokeProps} />
      <path d="M22 40h52M34 20v12M62 20v12" {...strokeProps} />
      <path d="M40 56l5 5 11-11" {...strokeProps} />
    </>
  ),
  plate: () => (
    <>
      <circle cx="48" cy="48" r="26" fill="currentColor" opacity="0.08" />
      <circle cx="48" cy="48" r="26" {...strokeProps} />
      <circle cx="48" cy="48" r="14" {...strokeProps} />
    </>
  ),
};

function Illustrations(props: { name: Illustration; class?: string }) {
  return (
    <svg viewBox="0 0 96 96" class={props.class} aria-hidden="true" role="presentation">
      {SHAPES[props.name]()}
    </svg>
  );
}

interface EmptyStateProps {
  /** Ilustración gastronómica a mostrar. */
  illustration?: Illustration;
  /** Título corto. */
  title: JSX.Element;
  /** Descripción opcional. */
  description?: JSX.Element;
  /** Acción opcional (botón/CTA). */
  action?: JSX.Element;
  /**
   * Cuando se provee, la ilustración se vuelve un objetivo interactivo que
   * dispara la misma acción que el CTA primario (abrir un diálogo, navegar o
   * enfocar un input), de modo que el empty-state nunca sea un callejón sin
   * salida. Tap/click/teclado (Enter/Space, nativos del `<button>`) lo activan.
   * Déjalo sin definir en empty-states puramente informativos (la ilustración
   * queda decorativa).
   */
  onIconActivate?: () => void;
  /**
   * `aria-label` del botón de la ilustración (recomendado cuando hay
   * `onIconActivate`). Debe describir la acción, p.ej. "Añade tu primera mesa".
   * Si se omite, cae al `title` cuando es texto.
   */
  iconActionLabel?: string;
  class?: string;
  "data-testid"?: string;
}

/**
 * Empty state consistente: ilustración SVG temática + texto + acción opcional.
 * Entrada suave (fade-in) que respeta `prefers-reduced-motion` vía el guard
 * global de global.css.
 *
 * Cuando se pasa `onIconActivate`, la ilustración pasa a ser un `<button>`
 * accesible (foco visible, hover sutil, `active:scale` solo bajo motion-safe)
 * que dispara el CTA primario; en empty-states informativos queda decorativa.
 */
export function EmptyState(props: EmptyStateProps) {
  const [local] = splitProps(props, [
    "illustration",
    "title",
    "description",
    "action",
    "onIconActivate",
    "iconActionLabel",
    "class",
    "data-testid",
  ]);

  return (
    <div
      data-testid={local["data-testid"]}
      class={cn(
        "flex animate-fade-in flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 bg-surface/40 px-6 py-16 text-center",
        local.class,
      )}
    >
      <Show
        when={local.onIconActivate}
        fallback={
          <div class="text-accent/70">
            <Illustrations name={local.illustration ?? "plate"} class="h-20 w-20" />
          </div>
        }
      >
        {(activate) => (
          <button
            type="button"
            aria-label={
              local.iconActionLabel ?? (typeof local.title === "string" ? local.title : undefined)
            }
            onClick={() => {
              activate()();
            }}
            class="group rounded-2xl p-1 text-accent/70 outline-none transition-[transform,color] duration-200 hover:text-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            <Illustrations name={local.illustration ?? "plate"} class="h-20 w-20" />
          </button>
        )}
      </Show>
      <p class="text-base font-semibold text-foreground">{local.title}</p>
      <Show when={local.description}>
        <p class="max-w-xs text-sm text-muted-foreground">{local.description}</p>
      </Show>
      <Show when={local.action}>
        <div class="mt-1">{local.action}</div>
      </Show>
    </div>
  );
}
